import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { db } from './firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import './SafetyTips.css';

// --- Helper function to get a random item from an array ---
const getRandomItem = (arr) => arr[Math.floor(Math.random() * arr.length)];

const SafetyTips = () => {
    const { t } = useTranslation();
    const [accidents, setAccidents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // --- Safety Tips Content ---
    const allTips = [
        {
            tipKey: 'safety_tip_seatbelt',
            fact: (data) => {
                const totalFatalities = data.reduce((sum, acc) => sum + (acc.totalFatalities > 0 ? acc.totalFatalities : (acc.fatalitiesMale || 0) + (acc.fatalitiesFemale || 0) + (acc.fatalitiesChildren || 0)), 0);
                return `There have been ${totalFatalities} fatalities recorded in verified accidents. Seatbelts save lives.`;
            }
        },
        {
            tipKey: 'safety_tip_no_speed',
            fact: (data) => {
                const highCasualtyAccidents = data.filter(acc => ((acc.totalInjuries || 0) + (acc.totalFatalities || 0)) > 5).length;
                const percentage = (highCasualtyAccidents / data.length * 100).toFixed(0);
                return `Over ${percentage}% of major accidents involve high speeds. Slow down.`;
            }
        },
        {
            tipKey: 'safety_tip_helmet',
            fact: (data) => {
                const motorcycleAccidents = data.filter(acc => acc.vehicles?.some(v => v.type === 'Motorcycle (Bodaboda)')).length;
                const percentage = (motorcycleAccidents / data.length * 100).toFixed(0);
                return `Motorcycles are involved in over ${percentage}% of reported accidents. A helmet is your most important gear.`;
            }
        },
        {
            tipKey: 'safety_tip_be_visible',
            fact: (data) => {
                // FIX: Removed the redundant .toDate() call which caused the error.
                // acc.datetime is already a JavaScript Date object here.
                const nightAccidents = data.filter(acc => acc.datetime && (acc.datetime.getHours() >= 18 || acc.datetime.getHours() < 6)).length;
                const percentage = (nightAccidents / data.length * 100).toFixed(0);
                return `${percentage}% of accidents occur at night. Wear bright clothing to be seen by drivers.`;
            }
        }
    ];

    const [dailyTip, setDailyTip] = useState(getRandomItem(allTips));

    useEffect(() => {
        const accidentsRef = collection(db, 'accidents');
        const q = query(accidentsRef, where("isVerified", "==", true));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const accidentsData = snapshot.docs.map(doc => ({ ...doc.data(), datetime: doc.data().datetime?.toDate() }));
            setAccidents(accidentsData);
            setIsLoading(false);
        }, (error) => {
            console.error("Error fetching data for safety tips: ", error);
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, []);
    
    // Function to get a new random tip
    const getNewTip = () => {
        setDailyTip(getRandomItem(allTips));
    };

    const funFact = useMemo(() => {
        if (accidents.length > 0) {
            return dailyTip.fact(accidents);
        }
        return "Loading safety data...";
    }, [dailyTip, accidents]);

    return (
        <div className="safety-tips-page">
            <div className="page-header">
                <h1>{t('safety_tips_title')}</h1>
                <p>{t('safety_tips_intro')}</p>
            </div>

            <div className="tip-of-the-day-container">
                <div className="tip-card animated">
                    <h3>{t('tip_of_the_day_heading')}</h3>
                    <p className="tip-text">"{t(dailyTip.tipKey)}"</p>
                    <div className="fun-fact">
                        <h4>{t('fun_fact_heading')}</h4>
                        <p>{isLoading ? 'Loading fact...' : funFact}</p>
                    </div>
                </div>
                <button onClick={getNewTip} className="new-tip-btn">{t('new_tip_button')}</button>
            </div>
        </div>
    );
};

export default SafetyTips;
